import React, { useState } from 'react';
import Slider from 'react-slick';
import {
    Box,
    Container,
    Typography,
    Paper,
    Rating,
    Avatar,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Popover
} from '@mui/material';
import { useAppLanguage } from '../hooks/useAppLanguage';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import GoogleIcon from '@mui/icons-material/Google';
import CloseIcon from '@mui/icons-material/Close';

// Import slick-carousel css
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import reviewsData from '../data/reviews.json';

interface Review {
    id: number;
    name: string;
    date: string;
    rating: number;
    text: string;
    initial: string;
    profile_photo_url?: string | null;
}

// Helper to depersonalize name
const depersonalizeName = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) {
        const firstName = parts[0];
        const lastNameInitial = parts[parts.length - 1].charAt(0);
        return `${firstName} ${lastNameInitial}.`;
    }
    return name;
};

// Extracted and Memoized Review Item Component
const ReviewItem = React.memo(({
    review,
    onClick,
    onMouseEnter,
    onMouseLeave
}: {
    review: Review,
    onClick: (review: Review) => void,
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>, review: Review) => void,
    onMouseLeave?: () => void
}) => {
    const theme = useTheme();
    const displayName = depersonalizeName(review.name);

    return (
        <Box sx={{ p: 2, height: '100%' }}>
            <Paper
                elevation={2}
                onClick={() => onClick(review)}
                onMouseEnter={(e) => onMouseEnter && onMouseEnter(e, review)}
                onMouseLeave={onMouseLeave}
                sx={{
                    p: 4,
                    height: '320px', // Fixed height for alignment
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 6,
                    }
                }}
            >
                <FormatQuoteIcon
                    sx={{
                        fontSize: 40,
                        color: theme.palette.primary.light,
                        opacity: 0.3,
                        position: 'absolute',
                        top: 16,
                        right: 16,
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                        sx={{ bgcolor: theme.palette.secondary.main, mr: 2 }}
                        src={review.profile_photo_url || undefined}
                        alt={displayName}
                    >
                        {!review.profile_photo_url && review.initial}
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {review.date}
                        </Typography>
                    </Box>
                </Box>

                <Rating value={review.rating} readOnly size="small" sx={{ mb: 2 }} />

                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        flexGrow: 1,
                        fontStyle: 'italic',
                        whiteSpace: 'pre-wrap', // Preserve newlines
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 7, // Limit lines to keep card neat
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    "{review.text}"
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GoogleIcon sx={{ color: '#4285F4' }} />
                    <Typography variant="caption" color="text.disabled" fontWeight="bold">
                        Google Review
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
});

// Content for Popovers/Dialogs (reusable, simple)
const ReviewFullContent = ({ review }: { review: Review }) => {
    const theme = useTheme();
    const displayName = depersonalizeName(review.name);

    return (
        <>
            <FormatQuoteIcon
                sx={{
                    fontSize: 40,
                    color: theme.palette.primary.light,
                    opacity: 0.3,
                    position: 'absolute',
                    top: 16,
                    right: 16,
                }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                    sx={{ bgcolor: theme.palette.secondary.main, mr: 2 }}
                    src={review.profile_photo_url || undefined}
                    alt={displayName}
                >
                    {!review.profile_photo_url && review.initial}
                </Avatar>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {review.date}
                    </Typography>
                </Box>
            </Box>
            <Rating value={review.rating} readOnly size="small" sx={{ mb: 2 }} />
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                    flexGrow: 1,
                    fontStyle: 'italic',
                    whiteSpace: 'pre-wrap',
                    overflow: 'visible',
                    maxHeight: 'none'
                }}
            >
                "{review.text}"
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <GoogleIcon sx={{ color: '#4285F4' }} />
                <Typography variant="caption" color="text.disabled" fontWeight="bold">
                    Google Review
                </Typography>
            </Box>
        </>
    );
};

const GoogleReviews: React.FC = () => {
    const theme = useTheme();
    const { t } = useAppLanguage();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [reviews, setReviews] = React.useState<Review[]>([]);

    // Interaction states
    const [selectedReview, setSelectedReview] = useState<Review | null>(null);
    const [hoveredReview, setHoveredReview] = useState<Review | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    React.useEffect(() => {
        // Filter reviews: remove empty texts and those mentioning "gift"
        const filtered = reviewsData.filter(review => {
            const text = review.text || '';
            if (!text.trim()) return false;
            return !text.toLowerCase().includes('gift');
        });

        // Randomize
        const shuffled = [...filtered].sort(() => 0.5 - Math.random());
        setReviews(shuffled.slice(0, 20));
    }, []);

    // Handlers
    const handleReviewClick = React.useCallback((review: Review) => {
        setSelectedReview(review);
        setAnchorEl(null);
        setHoveredReview(null);
    }, []);

    const handleDialogClose = () => {
        setSelectedReview(null);
    };

    const handlePopoverOpen = React.useCallback((event: React.MouseEvent<HTMLElement>, review: Review) => {
        if (!isMobile) {
            setAnchorEl(event.currentTarget);
            setHoveredReview(review);
        }
    }, [isMobile]);

    const handlePopoverClose = React.useCallback(() => {
        setAnchorEl(null);
        setHoveredReview(null);
    }, []);

    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        pauseOnHover: true, // Pause slider when hovering card
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: false, // Hide arrows on mobile for cleaner look
                }
            }
        ]
    };

    return (
        <Box sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
            <Container maxWidth="lg">
                <Typography
                    variant="h5"
                    component="h2"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 3
                    }}
                >
                    {t('googleReviews.title', 'What Our Patients Say')}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        mb: 6,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <GoogleIcon sx={{ fontSize: 32, color: '#4285F4' }} />
                            <Typography variant="h6" fontWeight="bold" color="text.primary">
                                Google
                            </Typography>
                        </Box>
                        <Box sx={{ width: 1, height: 24, bgcolor: 'divider' }} />
                        <Typography variant="h6" fontWeight="bold">
                            5.0
                        </Typography>
                        <Rating value={5} readOnly precision={0.5} />
                    </Box>

                    <Typography variant="body1" color="text.secondary">
                        {t('googleReviews.basedOn', { count: reviewsData.length, defaultValue: `Based on ${reviewsData.length} reviews` })}
                    </Typography>
                </Box>

                <Box sx={{ mx: { xs: 0, md: 4 } }}>
                    <Slider {...settings}>
                        {reviews.map((review) => (
                            <ReviewItem
                                key={review.id}
                                review={review}
                                onClick={handleReviewClick}
                                onMouseEnter={!isMobile ? handlePopoverOpen : undefined}
                                onMouseLeave={!isMobile ? handlePopoverClose : undefined}
                            />
                        ))}
                    </Slider>
                </Box>

                {/* Popover for Hover Effect (Desktop) */}
                <Popover
                    sx={{
                        pointerEvents: 'none', // Allow mouse to pass through so we don't block the slider if it misaligns
                    }}
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                    PaperProps={{
                        sx: {
                            p: 4,
                            width: anchorEl ? anchorEl.clientWidth : 300,
                            minHeight: '320px', // Match card min height
                            borderRadius: 4,
                            boxShadow: 24,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }
                    }}
                >
                    {hoveredReview && <ReviewFullContent review={hoveredReview} />}
                </Popover>

                {/* Dialog for Click Effect (Mobile & Desktop) */}
                <Dialog
                    open={Boolean(selectedReview)}
                    onClose={handleDialogClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 4, p: 2 }
                    }}
                >
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <GoogleIcon sx={{ color: '#4285F4', mr: 1 }} />
                            <Typography variant="h6" fontWeight="bold">
                                {t('googleReviews.reviewDetails', 'Review Details')}
                            </Typography>
                        </Box>
                        <IconButton onClick={handleDialogClose}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        {selectedReview && <ReviewFullContent review={selectedReview} />}
                    </DialogContent>
                </Dialog>

            </Container>
        </Box>
    );
};

export default GoogleReviews;
